import React, { useState } from 'react';
import { HbrSpinner } from '@harbor/elements-react-standalone';

import {
  useEducationRequiredSeries,
  type RequiredSeriesEntry,
  type RequiredSeriesReport,
  type RequiredSeriesStatus,
  type StatusCounts,
} from '../../../hooks/education-completion-hooks';
import StbAccordion from '../../security-toolbox-ui/StbAccordion';
import { progressPercent } from './EducationSeriesTable';

/**
 * Leader > Enablement > Team: required education BY SERIES, inside the "Education Completion — Team Rollup"
 * section. Each required series in the manager's org opens to their direct reports and each one's status. A
 * report who manages people opens to their own status plus a "Team" dropdown of status totals for everyone under
 * them (not counting them: their own status is shown above it).
 */

interface Props {
  managerEmail: string;
  /** Enablement time dropdown as a `time_filter`; it decides which series count as required. */
  timeFilter?: string;
}

const STATUS_LABEL: Record<RequiredSeriesStatus['status'], string> = {
  completed: 'Complete',
  in_progress: 'In progress',
  not_started: 'Not started',
};
const STATUS_COLOR: Record<RequiredSeriesStatus['status'], string> = {
  completed: '#15803d',
  in_progress: '#b45309',
  not_started: '#6b7280',
};

// Tall enough for roughly six collapsed series, with a sliver of the next showing that the list scrolls.
const LIST_MAX_HEIGHT = 420;
const CARD_CLASS = 'w-full min-w-0 bg-white border border-gray-400 rounded';

/** An accordion card whose body is only built once opened. */
function Card({ header, children }: { header: React.ReactNode; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <StbAccordion
      variant="minimal"
      className={CARD_CLASS}
      isOpen={open}
      toggleIsOpen={setOpen}
      headerContent={<div className="flex items-center flex-1 min-w-0">{header}</div>}
    >
      {open && <div style={{ padding: 12 }}>{children}</div>}
    </StbAccordion>
  );
}

function StatusText({ own }: { own: RequiredSeriesStatus | null }) {
  if (!own) return null;
  return (
    <span style={{ color: STATUS_COLOR[own.status], fontWeight: 600 }}>{STATUS_LABEL[own.status]}</span>
  );
}

function StatusDetail({ own }: { own: RequiredSeriesStatus | null }) {
  if (!own) return null;
  return (
    <span className="hbr-css__text-secondary-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
      {progressPercent(own)}% · {own.completed_modules} of {own.total_modules} modules
      {own.certified ? ` · ${own.certificate_name || 'Certified'}` : ''}
    </span>
  );
}

function CountBoxes({ counts }: { counts: StatusCounts }) {
  const boxes: Array<[string, number, string]> = [
    ['Completed', counts.completed, STATUS_COLOR.completed],
    ['In progress', counts.in_progress, STATUS_COLOR.in_progress],
    ['Not started', counts.not_started, STATUS_COLOR.not_started],
  ];
  return (
    <div className="flex flex-wrap" style={{ gap: 24 }}>
      {boxes.map(([label, value, color]) => (
        <div key={label}>
          <div style={{ fontSize: '1.25rem', fontWeight: 600, color, fontVariantNumeric: 'tabular-nums' }}>
            {value}
          </div>
          <div className="hbr-css__text-secondary-sm">{label}</div>
        </div>
      ))}
    </div>
  );
}

/** One line of totals for a card header. */
function CountsInline({ counts }: { counts: StatusCounts }) {
  return (
    <span className="hbr-css__text-secondary-sm" style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
      <span style={{ color: STATUS_COLOR.completed }}>{counts.completed} complete</span>
      {' · '}
      <span style={{ color: STATUS_COLOR.in_progress }}>{counts.in_progress} in progress</span>
      {' · '}
      <span style={{ color: STATUS_COLOR.not_started }}>{counts.not_started} not started</span>
    </span>
  );
}

/** A report belongs in the list when the series is theirs, or theirs to manage: someone on their team has it. */
const isInvolved = (r: RequiredSeriesReport): boolean => !!r.own || (!!r.team && r.team.assigned > 0);

function ReportRow({ report }: { report: RequiredSeriesReport }) {
  const name = `${report.email}${report.is_manager ? ' (Mgr)' : ''}`;
  const nameCell = (
    <div className="flex-1 min-w-0 truncate font-semibold" title={report.email}>
      {name}
    </div>
  );

  if (!report.is_manager) {
    return (
      <div className={`${CARD_CLASS} flex items-center px-4 py-2`} style={{ gap: 12 }}>
        {nameCell}
        <StatusDetail own={report.own} />
        <div style={{ width: 96, textAlign: 'right' }}>
          <StatusText own={report.own} />
        </div>
      </div>
    );
  }

  return (
    <Card
      header={
        <>
          {nameCell}
          <div style={{ width: 96, textAlign: 'right' }}>
            <StatusText own={report.own} />
          </div>
        </>
      }
    >
      <div className="flex flex-col" style={{ gap: 12 }}>
        {report.own && (
          <div className="flex items-center" style={{ gap: 12 }}>
            <span className="hbr-css__text-secondary-sm">Their own status:</span>
            <StatusText own={report.own} />
            <StatusDetail own={report.own} />
          </div>
        )}
        {report.team && report.team.assigned > 0 ? (
          <Card
            header={
              <>
                <div className="flex-1 min-w-0 font-semibold">Team ({report.team.assigned} assigned)</div>
                <CountsInline counts={report.team} />
              </>
            }
          >
            <CountBoxes counts={report.team} />
          </Card>
        ) : null}
      </div>
    </Card>
  );
}

function SeriesCard({ series }: { series: RequiredSeriesEntry }) {
  return (
    <Card
      header={
        <>
          <div className="flex-1 min-w-0 truncate font-semibold" title={series.series_name}>
            {series.series_name}
          </div>
          <CountsInline counts={series.team} />
        </>
      }
    >
      <div className="flex flex-col" style={{ gap: 8 }}>
        {series.reports.filter(isInvolved).map((r) => (
          <ReportRow key={r.email} report={r} />
        ))}
      </div>
    </Card>
  );
}

export default function EnablementRequiredSeriesRollup({ managerEmail, timeFilter }: Props) {
  const { data, isLoading, isError } = useEducationRequiredSeries(managerEmail, { timeFilter });
  const node = data?.node ?? null;
  // Only series assigned to someone on the team: a series only the manager holds is theirs, not the team's.
  const series = (node?.series ?? []).filter((s) => s.team.assigned > 0);

  let body: React.ReactNode;
  if (isLoading) {
    body = <HbrSpinner>Loading required series…</HbrSpinner>;
  } else if (isError || !node) {
    body = (
      <div className="hbr-css__text-secondary-sm">Required series by team is unavailable right now.</div>
    );
  } else if (!series.length) {
    body = <div className="hbr-css__text-secondary-sm">No required series are assigned in this team.</div>;
  } else {
    body = (
      <div style={{ maxHeight: LIST_MAX_HEIGHT, overflowY: 'auto' }}>
        <div className="flex flex-col" style={{ gap: 8 }}>
          {series.map((s) => (
            <SeriesCard key={s.series_id} series={s} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      <div>
        <div className="font-semibold">Required series by team</div>
        <div className="hbr-css__text-secondary-sm">
          Only series that are assigned are listed. Open a series to see each direct report’s status. Open a manager to
          see their own status and their team’s totals.
        </div>
      </div>
      {body}
    </div>
  );
}
